// Pure derivations for the live-calls surfaces. No React, no fetching.
import type { LiveCall, LiveCallDirection, LiveCallStatus } from './types';

export type Tone = 'good' | 'call' | 'warn' | 'crit' | 'off' | 'info';

export const STATUS_META: Record<LiveCallStatus, { label: string; tone: Tone; phase: 'ringing' | 'waiting' | 'talking' | 'hold' }> = {
  CALL_SETUP: { label: 'Setting up', tone: 'info', phase: 'ringing' },
  CUSTOMER_DIALING: { label: 'Dialing customer', tone: 'info', phase: 'ringing' },
  CUSTOMER_RINGING: { label: 'Customer ringing', tone: 'info', phase: 'ringing' },
  AGENT_DIALING: { label: 'Dialing agent', tone: 'info', phase: 'ringing' },
  AGENT_RINGING: { label: 'Agent ringing', tone: 'info', phase: 'ringing' },
  IN_IVR: { label: 'In IVR', tone: 'warn', phase: 'waiting' },
  IN_QUEUE: { label: 'In queue', tone: 'warn', phase: 'waiting' },
  HOLD: { label: 'On hold', tone: 'warn', phase: 'hold' },
  CONNECTED: { label: 'Connected', tone: 'good', phase: 'talking' },
};

export const DIRECTION_META: Record<LiveCallDirection, { label: string; short: string }> = {
  IN: { label: 'Inbound', short: 'In' },
  OUT: { label: 'Outbound', short: 'Out' },
  INT: { label: 'Internal', short: 'Int' },
};

export type PhaseFilter = 'all' | 'talking' | 'waiting' | 'ringing' | 'hold';
export const PHASE_FILTERS: { key: PhaseFilter; label: string }[] = [
  { key: 'all', label: 'Any status' },
  { key: 'talking', label: 'Talking' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'ringing', label: 'Ringing' },
  { key: 'hold', label: 'On hold' },
];

export function statusMeta(status: string | null | undefined) {
  return STATUS_META[status as LiveCallStatus] || { label: status || 'Unknown', tone: 'off' as Tone, phase: 'ringing' as const };
}

/** mm:ss or h:mm:ss from a millisecond span; never negative. */
export function fmtSpan(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function sinceMs(now: number, at: number | null | undefined): number | null {
  if (!at) return null;
  return now - at;
}

export interface LiveCallCounters {
  total: number;
  talking: number;
  waiting: number;
  ringing: number;
  hold: number;
  IN: number;
  OUT: number;
  INT: number;
  transfers: number;
  snooped: number;
}

export function countCalls(calls: LiveCall[]): LiveCallCounters {
  const c: LiveCallCounters = { total: calls.length, talking: 0, waiting: 0, ringing: 0, hold: 0, IN: 0, OUT: 0, INT: 0, transfers: 0, snooped: 0 };
  for (const call of calls) {
    c[statusMeta(call.status).phase] += 1;
    if (call.direction && c[call.direction] !== undefined) c[call.direction] += 1;
    if (call.transfer) c.transfers += 1;
    if (call.snoopers > 0) c.snooped += 1;
  }
  return c;
}

export function filterByPhase(calls: LiveCall[], phase: PhaseFilter): LiveCall[] {
  if (phase === 'all') return calls;
  return calls.filter((c) => statusMeta(c.status).phase === phase);
}

export function filterByText(calls: LiveCall[], text: string): LiveCall[] {
  const q = text.trim().toLowerCase();
  if (!q) return calls;
  const digits = q.replace(/\D/g, '');
  return calls.filter((c) => {
    const names = [
      c.customerName,
      c.agent?.name,
      c.calleeAgent?.name,
      c.transfer?.toAgent?.name,
      c.queueName,
      c.campaignId,
    ]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase());
    if (names.some((n) => n.includes(q))) return true;
    if (digits && ((c.customerNo || '').includes(digits) || (c.did || '').includes(digits))) return true;
    return false;
  });
}

export interface LiveCallAlert {
  id: string;
  tone: 'warn' | 'crit';
  text: string;
  callId: string;
}

/** Thresholds match the team-lead alerts bar: long ring, long hold, transfer in flight. */
export const ALERT_RING_MS = 8_000;
export const ALERT_HOLD_MS = 45_000;
export const ALERT_QUEUE_MS = 60_000;

export function buildAlerts(calls: LiveCall[], now: number): LiveCallAlert[] {
  const out: LiveCallAlert[] = [];
  for (const c of calls) {
    const inStatus = sinceMs(now, c.statusSince) ?? 0;
    const who = c.agent?.name || c.agent?.ext || 'Agent';
    const cust = c.customerNo || ''; // never the DID in the customer slot
    if (c.status === 'AGENT_RINGING' && inStatus > ALERT_RING_MS) {
      out.push({ id: `${c.id}:ring`, tone: 'warn', text: `${who} ringing ${fmtSpan(inStatus)} · ${cust}`, callId: c.id });
    }
    if (c.status === 'HOLD' && inStatus > ALERT_HOLD_MS) {
      out.push({ id: `${c.id}:hold`, tone: 'crit', text: `${who} holding ${cust} for ${fmtSpan(inStatus)}`, callId: c.id });
    }
    if (c.status === 'IN_QUEUE' && inStatus > ALERT_QUEUE_MS) {
      out.push({ id: `${c.id}:queue`, tone: 'crit', text: `${cust} waiting ${fmtSpan(inStatus)} in ${c.queueName || 'queue'}`, callId: c.id });
    }
    if (c.transfer && c.transfer.state !== 'JOINED') {
      const target = c.transfer.toAgent?.name || c.transfer.toNumber || '…';
      out.push({ id: `${c.id}:xfer`, tone: 'warn', text: `Transfer ${String(c.transfer.state || '').toLowerCase()}: ${who} → ${target}`, callId: c.id });
    }
  }
  return out;
}

/** Group a team-lead page by call group. Calls with no group land under "Unassigned". */
export function groupByCallGroup(
  calls: LiveCall[],
  groups: { callGroupId: number; name: string; agentIds: number[] }[] | null | undefined,
) {
  const byId = new Map<number, { callGroupId: number; name: string; agentIds: number[]; calls: LiveCall[] }>();
  for (const g of groups || []) byId.set(g.callGroupId, { ...g, calls: [] });
  const unassigned: LiveCall[] = [];
  for (const c of calls) {
    const ids = c.callGroupIds || [];
    if (!ids.length) {
      unassigned.push(c);
      continue;
    }
    for (const id of ids) {
      const bucket = byId.get(id);
      if (bucket) bucket.calls.push(c);
      else byId.set(id, { callGroupId: id, name: `Group ${id}`, agentIds: [], calls: [c] });
    }
  }
  const list = [...byId.values()];
  if (unassigned.length) list.push({ callGroupId: 0, name: 'Unassigned', agentIds: [], calls: unassigned });
  return list;
}

/**
 * Adapter so a LiveCall row can drive cti2's useSnoop / SnoopButtons, which
 * expect the roster row shape ({ agent, name, snoop: { answered, callId, ready },
 * dashboard }). asterisk_id is not on the live row (it carries the PBX name);
 * useSnoop falls back to "1" until CT API confirms the mapping.
 */
export function toSnoopRow(call: LiveCall) {
  const answered = call.status === 'CONNECTED';
  const callId = call.agent?.callId || null;
  return {
    id: call.agent?.id || call.id,
    agent: call.agent ? { id: call.agent.id } : null,
    name: call.agent?.name || call.agent?.ext || 'Agent',
    groupId: call.callGroupIds?.[0] || null,
    dashboard: { pbx_name: call.pbx },
    snoop: { answered, callId, ready: answered && Boolean(callId) },
  };
}

export function hangupChannelId(call: LiveCall): string | null {
  return call.customerCallId || call.agent?.callId || null;
}

/** Body for POST /agents/mobile-hangup with source=live_call_hangup. */
export function buildHangupBody(call: LiveCall) {
  return {
    source: 'live_call_hangup',
    channelId: hangupChannelId(call),
    companyId: call.companyId,
    ccId: call.ccId,
    pbx_name: call.pbx,
    agent_id: call.agent?.id || undefined,
    requestId: crypto.randomUUID(),
    causeCode: 16,
  };
}
